/**
 * Screening  service
 *
 */
app.factory('BucketService', function($http, $q) {
    var bucketService = {};

    /*
     * Find a matching bucket for the provided tickettype, now and roles.
     *
     * At first we try to find a matching, non-full bucket. Then if the search
     * failed, we fallback on the matching's may_steal_from buckets but without the
     * matching check. If stealing also fails, we callback() without an assigned
     * booking.
     *
     * @param buckets
     *   A screening bucket, expected to have been "expanded" by
     *   _buckets_expanded().
     *
     * @param tickettype
     *   The pledged tickettype _id, used by some bucket rules.
     *
     * @param now
     *   Date.now(), used because some bucket rules check when the booking is
     *   requested.
     *
     * @param roles
     *   the requestor roles, used because some bucket rules check them
     *
     * @param categoryOrCallback
     *   Optional pledged ticket:activated_pricing:category, or the callback
     *   when no category is provided (legacy signature).
     *
     * @param callback(err, assigned) optional
     *   yield (err, null) on error, (null, null) on failure and (null, assigned)
     *   on success, where assigned is an aobject having the
     *   screening_bucket property set to: {matching: m_id, assigned: a_id}
     *
     *   m_id === a_id when we found a matching non-full bucket, otherwise
     *   the bucket m_id stole from a_id.
     *
     * @return Object if no callback provided (see callback assigned parameter)
     */
    bucketService.assign = function (buckets, tickettype, now, roles, categoryOrCallback, callback) {
        var category = null;
        if (typeof categoryOrCallback === 'function') {
            callback = categoryOrCallback;
        } else {
            category = categoryOrCallback;
        }
        // array of potential screening_bucket
        var forcing  = [];
        var bucketed = {};

        // look through each bucket for a matching, non-full one.
        var match = _.find(buckets, function (bucket) {
            var is_match = bucketService._match(bucket, [tickettype], now, roles, category);
            var is_full  = (bucket.available <= 0);
            if (is_match && is_full) {
                // for each bucket we may steal from, add a potential
                // screening_bucket to the forcing list.
                bucket.may_steal_from.forEach(function (victim_id) {
                    forcing.push({
                        matching: bucket._id,
                        assigned: victim_id,
                    });
                });
            }
            return (is_match && !is_full);
        });
        if (match) {
            bucketed.screening_bucket = {
                matching: match._id,
                assigned: match._id,
            };
            return callback ? callback(null, bucketed) : bucketed;
        }

        // no matching bucket had place left, try to steal from the first non-empty
        // victims.
        var forced = _.find(forcing, function (force) {
            var victim = _.findWhere(buckets, {_id: force.assigned});
            return (victim && victim.available > 0);
        });
        if (forced) {
            bucketed.screening_bucket = forced;
            return callback ? callback(null, bucketed) : bucketed;
        }

        // stealing failed, notify callback accordingly.
        return callback ? callback(null, null) : null;
    },

    /*
     * Find a matching bucket for the provided tickettype, now and roles.
     *
     * At first we try to find a matching, non-full bucket. Then if the search
     * failed, we fallback on the matching's may_steal_from buckets but without the
     * matching check. If stealing also fails, we return null
     *
     * @param buckets
     *   A screening bucket, expected to have been "expanded" by
     *   _buckets_expanded().
     *
     * @param tickettype
     *   The pledged tickettype _id, used by some bucket rules.
     *
     * @param now
     *   Date.now(), used because some bucket rules check when the booking is
     *   requested.
     *
     * @param roles
     *   the requestor roles, used because some bucket rules check them
     *
     * @return Object on success, having the screening_bucket property set
     * 	       to: {matching: m_id, assigned: a_id}, null otherwise.
     */
    bucketService.sync_assign = function (buckets, tickettype, now, roles, category) {
        // array of potential screening_bucket
        var forcing  = [];
        var bucketed = {};

        // look through each bucket for a matching, non-full one.
        var match = _.find(buckets, function (bucket) {
            var is_match = bucketService._match(bucket, [tickettype], now, roles, category);
            var is_full  = (bucket.available <= 0);
            if (is_match && is_full) {
                // for each bucket we may steal from, add a potential
                // screening_bucket to the forcing list.
                bucket.may_steal_from.forEach(function (victim_id) {
                    forcing.push({
                        matching: bucket._id,
                        assigned: victim_id,
                    });
                });
            }
            return (is_match && !is_full);
        });
        if (match) {
            bucketed.screening_bucket = {
                matching: match._id,
                assigned: match._id,
            };
            return bucketed;
        }

        // no matching bucket had place left, try to steal from the first non-empty
        // victims.
        var forced = _.find(forcing, function (force) {
            var victim = _.findWhere(buckets, {_id: force.assigned});
            return (victim && victim.available > 0);
        });
        if (forced) {
            bucketed.screening_bucket = forced;
            return bucketed;
        }

        // stealing failed
        return null;
    },

    /*
     * Get the number of available seats for the provided tickettype, now and roles.
     *
     * @param buckets
     *   A screening bucket, expected to have been "expanded" by
     *   _buckets_expanded().
     *
     * @param tickettypes
     *   The pledged tickettypes _ids, used by some bucket rules.
     *
     * @param now
     *   Date.now(), used because some bucket rules check when the booking is
     *   requested.
     *
     * @param roles
     *   the requestor roles, used because some bucket rules check them
     *
     * @return int: The number of available seats
     */
    bucketService.get_available_seats = function (buckets, tickettypes, now, roles, category, options) {
        var matchable_buckets = buckets.filter(function (bucket) {
            return bucketService._match(bucket, tickettypes, now, roles, category, options);
        });

        var stealable_ids = _.uniq(_.flatten(_.pluck(matchable_buckets, 'may_steal_from')));

        return buckets.reduce(function (memo, bucket) {
            if (_.contains(_.pluck(matchable_buckets, '_id'), bucket._id))
                return memo + bucket.available;

            if (_.contains(stealable_ids, bucket._id))
                return memo + bucket.available;

            return memo;
        }, 0);
    };

    /*
     * Available seats for map / listing when no concrete seat category is pledged.
     *
     * Unions unrestricted matches with matches for each OTP pricing pledge
     * category (mirrors Screening::bookable_buckets_for_availability).
     *
     * @param pledge_categories string[] categories implied by sellable OTP rates
     * @param options optional { ignore_too_early: true } to skip bucket not_before
     *   (TIME_FROM_NOW equivalent).
     */
    bucketService.get_available_seats_for_availability = function (
        buckets, tickettypes, now, roles, pledge_categories, options
    ) {
        var has_category_buckets = _.some(buckets, function (bucket) {
            return bucket.rules && ('only_for_categories' in bucket.rules);
        });
        if (!has_category_buckets) {
            return bucketService.get_available_seats(buckets, tickettypes, now, roles, null, options);
        }

        var matchable_ids = {};
        var matchable_buckets = [];
        var consider = function (category) {
            _.each(buckets, function (bucket) {
                if (matchable_ids[bucket._id]) {
                    return;
                }
                if (bucketService._match(bucket, tickettypes, now, roles, category, options)) {
                    matchable_ids[bucket._id] = true;
                    matchable_buckets.push(bucket);
                }
            });
        };

        consider(null);
        _.each(pledge_categories || [], function (cat) {
            if (cat) {
                consider(cat);
            }
        });

        var stealable_ids = _.uniq(_.flatten(_.pluck(matchable_buckets, 'may_steal_from')));

        return buckets.reduce(function (memo, bucket) {
            if (matchable_ids[bucket._id] || _.contains(stealable_ids, bucket._id)) {
                return memo + bucket.available;
            }
            return memo;
        }, 0);
    };

    /*
     * Earliest future bucket not_before among buckets that would match if
     * TIME_FROM_NOW (ignore too-early). Returns a moment or null.
     */
    bucketService.next_opening_at = function (buckets, tickettypes, now, roles, pledge_categories) {
        var nowMoment = moment(now);
        var next = null;
        var consider = function (category) {
            _.each(buckets, function (bucket) {
                if (!bucketService._match(bucket, tickettypes, now, roles, category, { ignore_too_early: true })) {
                    return;
                }
                if (!bucket.rules || !bucket.rules.not_before) {
                    return;
                }
                var opening = moment(bucket.rules.not_before);
                if (!opening.isAfter(nowMoment)) {
                    return;
                }
                if (!next || opening.isBefore(next)) {
                    next = opening;
                }
            });
        };

        consider(null);
        _.each(pledge_categories || [], function (cat) {
            if (cat) {
                consider(cat);
            }
        });

        return next;
    };

    bucketService._for_each_availability_match = function (
        buckets, tickettypes, now, roles, pledge_categories, options, fn
    ) {
        var seen = {};
        var consider = function (category) {
            _.each(buckets || [], function (bucket) {
                if (seen[bucket._id]) {
                    return;
                }
                if (bucketService._match(bucket, tickettypes, now, roles, category, options)) {
                    seen[bucket._id] = true;
                    fn(bucket);
                }
            });
        };
        consider(null);
        _.each(pledge_categories || [], function (cat) {
            if (cat) {
                consider(cat);
            }
        });
    };

    bucketService.has_matching_buckets = function (
        buckets, tickettypes, now, roles, pledge_categories, options
    ) {
        var found = false;
        bucketService._for_each_availability_match(
            buckets, tickettypes, now, roles, pledge_categories, options || {},
            function () { found = true; }
        );
        return found;
    };

    /*
     * Latest bucket not_after already in the past among buckets that would
     * match if TIME_ABSOLUTE (ignore too-late). Returns a moment or null.
     */
    bucketService.last_closing_at = function (buckets, tickettypes, now, roles, pledge_categories) {
        var nowMoment = moment(now);
        var last = null;
        bucketService._for_each_availability_match(
            buckets, tickettypes, now, roles, pledge_categories, { ignore_too_late: true },
            function (bucket) {
                if (!bucket.rules || !bucket.rules.not_after) {
                    return;
                }
                var closing = moment(bucket.rules.not_after);
                if (closing.isAfter(nowMoment)) {
                    return;
                }
                if (!last || closing.isAfter(last)) {
                    last = closing;
                }
            }
        );
        return last;
    };

    bucketService.get_total_seats = function (buckets) {
        return buckets.reduce(function (memo, bucket) {
            return memo + bucket.total_capacity;
        }, 0);
    }

    bucketService.get_unconfirmed_seats = function (buckets) {
        return buckets.reduce(function (memo, bucket) {
            return memo + bucket.unconfirmed;
        }, 0);
    }

    bucketService.get_confirmed_seats = function (buckets) {
        return buckets.reduce(function (memo, bucket) {
            return memo + bucket.confirmed;
        }, 0);
    }

    bucketService.get_used_seats = function (buckets) {
        return buckets.reduce(function (memo, bucket) {
            return memo + bucket.scanned;
        }, 0);
    }

    /*
     * return true if the given tickettype, now and roles match all bucket's rules, false otherwise.
     *
     * @param category optional pledged ticket:activated_pricing:category
     *   When only_for_categories is present, a missing pledge does not match.
     */
    bucketService._match = function (bucket, tickettypes, now, roles, category, options) {
        if (tickettypes && !Array.isArray(tickettypes))
            tickettypes = [tickettypes];

        options = options || {};
        var rules = bucket.rules || {};
        // not_after, checked first because it is the rule that should be used for
        // "stash" buckets. TIME_ABSOLUTE skips this (sales already closed still match).
        if (!options.ignore_too_late && 'not_after' in rules && moment(now).isAfter(rules.not_after))
            return false;
        // not_before (TIME_FROM_NOW skips this — sales that open later still match)
        if (!options.ignore_too_early && 'not_before' in rules && moment(now).isBefore(rules.not_before))
            return false;
        // only_for_tickettypes
        if (tickettypes && ('only_for_tickettypes' in rules)) {
            var intersection = _.intersection(rules.only_for_tickettypes, tickettypes);
            if (intersection.length === 0)
                return false;
        }
        // only_for_roles
        if ('only_for_roles' in rules) {
            var intersection = _.intersection(rules.only_for_roles, roles);
            if (intersection.length === 0)
                return false;
        }
        // only_for_categories — missing pledge = no match when rule present
        if ('only_for_categories' in rules) {
            if (!category || !_.contains(rules.only_for_categories, category))
                return false;
        }

        // all checks have passed.
        return true;
    }

    return bucketService;
});
